import '../Styles/App.css';
import { Element } from 'react-scroll';

import ReusableAppBar from '../Components/UI/ReusableAppBar';
import Footer from '../Components/Layout/Footer';
import ProjectTemplate from '../Components/Projects/ProjectTemplate';

// ⚠️ replace this with a real SDS cover later if you have one
import CoverImage from '../Assets/Images/movemint cover.jpg';

import HeroGif from '../Assets/Gifs/SevenDeadlySpins.gif';
import gallery1 from '../Assets/Images/SevenDeadlySpins (1).png';
import gallery2 from '../Assets/Images/SevenDeadlySpins (2).png';
import gallery3 from '../Assets/Images/SevenDeadlySpins (3).png';
import gallery4 from '../Assets/Images/SevenDeadlySpins (4).png';
import gallery5 from '../Assets/Images/SevenDeadlySpins (6).png';

function SevenDeadlySpins() {
  return (
    <main>
      <ReusableAppBar />

      <ProjectTemplate
        CoverImageSRC={CoverImage}
        HeroGif={HeroGif}
        ProjectTitle="Seven Deadly Spins"
        ProjectSummary="3D arcade FPS built for PegJam 2025 featuring a slot machine weapon system that creates unpredictable, time-based combat."
        ProjectLink="https://dobymick.itch.io/sevendeadlyspins"
        ProjectRole="Gameplay Programmer / Co-Developer"
        ProjectTimeFrame="72 Hours"
        ProjectDemo="https://www.youtube.com/embed/upqhG9RV9vg"
        ProjectType="Game Jam Game"
        ProjectEngine="Unity"
        ProjectLanguage="C#"
        ProjectTeamSize="2 Programmers 1 Artist"
        BuildStatus="Completed playable build"

        TechStack={[
          'Unity',
          'C#',
          '3D FPS Systems',
          'Enemy AI & Spawning',
          'Billboard Rendering',
          'Audio Design',
          'Gameplay Systems Integration',
        ]}

        Overview="Seven Deadly Spins is a 3D arcade-style FPS created for PegJam 2025 around the theme of unconventional weapons. The core mechanic is a slot machine weapon system: a slot machine appears in front of the player, and spinning it until matching three symbols grants a temporary weapon.

Each combination results in a different weapon behaviour, such as a rocket-launcher-style attack or a long-range precision shot. Once activated, the weapon lasts for a limited time before the player must spin again, creating a loop of risk, timing, and adaptation.

To support this system, the game blends 2D sprite-based enemies into a 3D environment using billboard rendering, allowing strong 2D art to function clearly in 3D space. The result is a fast-paced and unpredictable combat experience driven by randomness and player decision-making."

        Contributions={[
          'Implemented player movement and core FPS controls.',
          'Developed enemy behaviour systems and spawning logic.',
          'Created a billboard rendering system to display 2D sprites correctly in 3D space.',
          'Built the player health system and UI feedback.',
          'Designed and implemented all game audio and sound effects.',
          'Integrated gameplay systems to support the slot machine weapon loop.',
        ]}

        CollaborationNote="This project was developed in a team of three during PegJam 2025. I worked closely with Eli Heinrichs and Quinn Davis, contributing primarily to gameplay programming and systems integration while collaborating on overall design direction."

        ProgrammingConcepts={[
          {
            title: 'Billboard Rendering in 3D Space',
            description:
              'To integrate 2D artwork into a 3D environment, I implemented a billboard system that ensures sprites always face the player camera.',
          },
          {
            title: 'Enemy Behaviour & Spawning Systems',
            description:
              'Enemies were built with simple behaviour loops and combined with a spawning system that supports pacing and gameplay pressure.',
          },
          {
            title: 'System Integration with External Mechanics',
            description:
              'I integrated movement, enemies, UI, and audio around the slot machine weapon system to ensure a cohesive gameplay loop.',
          },
          {
            title: 'Audio-Driven Game Feel',
            description:
              'Sound effects were implemented to reinforce combat and slot machine feedback, significantly improving polish.',
          },
          {
            title: 'Rapid Development & Prioritization',
            description:
              'Development focused on delivering a small number of well-functioning systems under strict time constraints.',
          },
        ]}

        Challenges={[
          {
            challenge:
              'The core weapon system was highly dynamic and required other gameplay systems to adapt around it.',
            solution:
              'I ensured enemy, movement, and UI systems were flexible enough to support constantly changing weapon states.',
          },
          {
            challenge:
              'The team wanted a 3D game, but our strongest art direction was in 2D sprites.',
            solution:
              'I implemented a billboard system so sprites always face the camera, allowing 2D assets to function in 3D.',
          },
          {
            challenge:
              'The project needed to feel polished within a short game jam timeframe.',
            solution:
              'I prioritized audio and system integration to maximize perceived polish.',
          },
        ]}

        CompletionLevel="This project resulted in a fully playable and polished game jam build with strong system integration and responsive gameplay."

        KnownLimitations="Due to time constraints, systems were kept intentionally simple, with focus placed on delivering a cohesive core experience."

        GameplayNote="The gameplay revolves around spinning the slot machine to obtain temporary weapons, with each result forcing the player to adapt their strategy in real time."

        CodeSamples={[
          {
            title: 'Wave-based Enemy Generation',
            code: `
    private void StartWave()
    {
        int round = GameManager.Instance.currentRound;
        enemiesLeft = (round + 1) * 3;
        spawning = true;
        StartCoroutine(SpawnEnemies());
    }

    private IEnumerator SpawnEnemies()
    {
        while (enemiesLeft > 0 && spawning)
        {
            yield return new WaitForSeconds(5f);
            
            GameObject enemyPrefab = enemyPrefabs[Random.Range(0, enemyPrefabs.Count)];
            Transform spawnPoint = spawnPoints[Random.Range(0, spawnPoints.Count)];

            Instantiate(enemyPrefab, spawnPoint.position, spawnPoint.rotation);

            enemiesLeft--;
        }
    }`,
            notes: [
              'Handles enemy spawning per round.',
              'Simple scaling system suitable for game jam scope.',
            ],
          },
          {
            title: 'Player Health UI',
            code: `
    private void UpdateHearts()
    {
        for (int i = 0; i < hearts.Length; i++)
        {
            if (i < currentHealth)
                hearts[i].color = Color.white;
            else
                hearts[i].color = Color.black;
        }
    }`,
            notes: [
              'Updates health UI dynamically.',
              'Simple and effective for jam scope.',
            ],
          },
        ]}

        GalleryImages={[gallery1, gallery2, gallery3, gallery4, gallery5]}

        Reflection="Seven Deadly Spins stands out as one of my most polished game jam projects. Strong team coordination and a clear core idea allowed us to move quickly and execute effectively within the time constraint.

This project reinforced the importance of system integration and how impactful audio can be in improving game feel. While not my most technically complex project, it is one of my best examples of collaboration and delivering a cohesive experience under pressure."
      />

      <Element name="Contact">
        <Footer />
      </Element>
    </main>
  );
}

export default SevenDeadlySpins;